import Actions from '../../redux/Actions';
import Store from '../../redux/Store';
import Config from '../../globals/Config';
import HttpService from '../HttpService/HttpService';
import NotificationSettingsModel, {filterNotificationSetting} from '../../types/NotificationSettingsModel';
import GoogleAnalyticsService from '../GoogleAnalyticsService/GoogleAnalyticsService';
import Constants, {ModalStates, UserTypes, SortMetric, NotificationTypes} from '../../globals/Constants';
import AddReviewerGroupModel from '../../types/AddReviewerGroupModel';
import ReviewerGroupModel from '../../types/ReviewerGroupModel';
import CreatedUserModel from '../../types/CreatedUserModel';
import UserModel, {nullUserFactory} from '../../types/UserModel';
import LocalStorageWrapper from '../LocalStorageWrapper/LocalStorageWrapper';

const baseUrl = Config.apiEndPoint + Config.apiPrefix;
const notificationsBaseUrl = Config.notificationEndPointUri + Config.notificationApiPrefix;
let hasInitializedUser = false;

const reportNotificationChangeToGA = (oldNotificationSettings: NotificationSettingsModel[], newNotificationSettings: NotificationSettingsModel): void => {
   const userId = Store.getState().currentUserId;

   const oldCommentSetting: NotificationSettingsModel = filterNotificationSetting(oldNotificationSettings, NotificationTypes.NewComment);
   const oldCommentReplySetting: NotificationSettingsModel = filterNotificationSetting(oldNotificationSettings, NotificationTypes.NewCommentReply);

   if (newNotificationSettings.eventType === NotificationTypes.NewComment && oldCommentSetting.enabled !== newNotificationSettings.enabled) {
      const value = newNotificationSettings.enabled ? Constants.googleAnalytics.preferenceChange.labels.enabled : Constants.googleAnalytics.preferenceChange.labels.disabled;
      GoogleAnalyticsService.trackPreferenceChanged(userId, Constants.googleAnalytics.preferenceChange.actions.commentNotification, value);
   }

   if (newNotificationSettings.eventType === NotificationTypes.NewComment && oldCommentReplySetting.enabled !== newNotificationSettings.enabled) {
      const value = newNotificationSettings.enabled ? Constants.googleAnalytics.preferenceChange.labels.enabled : Constants.googleAnalytics.preferenceChange.labels.disabled;
      GoogleAnalyticsService.trackPreferenceChanged(userId, Constants.googleAnalytics.preferenceChange.actions.replyNotification, value);
   }
};

const setupSelfIdentifiedUserFromLocalStorage = (): void => {
   const {selfIdentifiedId, selfIdentifiedName, selfIdentifiedSecret} = Constants.localStorageKeys;
   const userId = LocalStorageWrapper.getItem(selfIdentifiedId);
   const secret = LocalStorageWrapper.getItem(selfIdentifiedSecret);
   const name = LocalStorageWrapper.getItem(selfIdentifiedName);

   if (userId && secret && name) {
      Store.dispatch(Actions.addOrUpdateUser(nullUserFactory({userId: userId, name: name, userType: UserTypes.selfIdentified})));
      Store.dispatch(Actions.setUserId(userId));
      Config.user.secret = secret;
   }
};

const forgetSelfIdentifiedUser = (): void => {
   const {selfIdentifiedId, selfIdentifiedName, selfIdentifiedSecret} = Constants.localStorageKeys;
   LocalStorageWrapper.removeItem(selfIdentifiedId);
   LocalStorageWrapper.removeItem(selfIdentifiedName);
   LocalStorageWrapper.removeItem(selfIdentifiedSecret);
   Store.dispatch(Actions.setUserId(''));
   Config.user.secret = '';
};

const initializeFeedbackUser = async (): Promise<void> => {
   if (hasInitializedUser) {
      return;
   }

   hasInitializedUser = true;
   let currentTheme;
   let enableCommentHotkey;

   if (Config.user.isAuthenticated) {
      currentTheme = Config.user.theme && Config.user.theme.toLowerCase();
      enableCommentHotkey = Config.user.enableCommentHotkey;
      if (LocalStorageWrapper.getItem(Constants.localStorageKeys.selfIdentifiedId)) {
         GoogleAnalyticsService.trackGuestUserLogInWithTechSmithAccount(Config.user.techSmithId, Constants.googleAnalytics.guestUserLogIn.action);
      }
      forgetSelfIdentifiedUser();
   } else {
      setupSelfIdentifiedUserFromLocalStorage();
      currentTheme = LocalStorageWrapper.getItem(Constants.localStorageKeys.themeName);
   }

   currentTheme = currentTheme || Constants.themeName.default;

   if (Store.getState().themeName !== currentTheme) {
      Store.dispatch(Actions.setInterfaceTheme(currentTheme));
   }

   Store.dispatch(Actions.setIsCommentHotkeyEnabled(enableCommentHotkey));

   loadReviewsSortMetric();
};

const loadReviewsSortMetric = () => {
   const localStorageKey = LocalStorageWrapper.getKey(Constants.localStorageKeys.reviewsSortMetric, Config.user.techSmithId);
   const sortMetric = LocalStorageWrapper.getItem(localStorageKey) as SortMetric;
   if (sortMetric) {
      Store.dispatch(Actions.setReviewsSortMetric(sortMetric));
   }
};

const setSelfIdentifiedUser = async (displayName: string): Promise<void> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithMultipleAuths(`${baseUrl}/users`, {DisplayName: displayName}), 'SetSelfIdentifiedUser', Constants.statusCodes.created);
   const returnedUser: CreatedUserModel = response.data;
   const user: UserModel = nullUserFactory({userId: returnedUser.Id, name: returnedUser.DisplayName, userType: UserTypes.selfIdentified});
   LocalStorageWrapper.setItem(Constants.localStorageKeys.selfIdentifiedId, user.userId);
   LocalStorageWrapper.setItem(Constants.localStorageKeys.selfIdentifiedName, user.name);
   LocalStorageWrapper.setItem(Constants.localStorageKeys.selfIdentifiedSecret, returnedUser.Secret);
   Store.dispatch(Actions.addOrUpdateUser(user));
   Store.dispatch(Actions.setUserId(user.userId));
   Config.user.secret = returnedUser.Secret;
   Store.dispatch(Actions.setModalState({modalState: ModalStates.closed}));
};

const postAcceptTermsOfService = async (): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithTscAuthToken(`${baseUrl}/users/me/accepttermsofservice`, null), 'PostAcceptTermsOfService', Constants.statusCodes.ok);
};

const addReviewerGroup = async (reviewerGroup: AddReviewerGroupModel): Promise<ReviewerGroupModel[]> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithTscAuthToken(`${baseUrl}/users/me/groups`, reviewerGroup), 'AddReviewerGroup', Constants.statusCodes.ok);
   return getReviewerGroups();
};

const deleteReviewerGroup = async (id: string): Promise<ReviewerGroupModel[]> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithTscAuthToken(`${baseUrl}/users/me/groups/${id}`), 'DeleteReviewerGroup', Constants.statusCodes.ok);
   return getReviewerGroups();
};

const addReviewerToGroup = async (groupId: string, email: string): Promise<ReviewerGroupModel[]> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithTscAuthToken(`${baseUrl}/users/me/groups/${groupId}`, {Email: email}), 'AddReviewerToGroup', Constants.statusCodes.ok);
   return getReviewerGroups();
};

const updateReviewerGroupName = async (groupId: string, newName: string): Promise<ReviewerGroupModel[]> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/users/me/groups/${groupId}`, {newGroupName: newName}), 'UpdateReviewerGroup', Constants.statusCodes.ok);
   return getReviewerGroups();
};

const getReviewersFromGroup = async (groupName: string): Promise<ReviewerGroupModel[]> => {
   const encodedGroupName = encodeURIComponent(groupName);
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithTscAuthToken(`${baseUrl}/users/me/groups/${encodedGroupName}`), 'GetReviewersFromGroup', Constants.statusCodes.ok);
   return response.data.GroupMembers;
};

const deleteReviewerFromGroup = async (groupId: string, reviewerId: string): Promise<ReviewerGroupModel[]> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithTscAuthToken(`${baseUrl}/users/me/groups/${groupId}/${reviewerId}`), 'DeleteReviewerFromGroup', Constants.statusCodes.ok);
   return getReviewerGroups();
};

const getReviewerGroups = async (): Promise<ReviewerGroupModel[]> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithTscAuthToken(`${baseUrl}/users/me/groups`), 'GetReviewerGroups', Constants.statusCodes.ok);
   const groups = response.data.Groups || [];
   Store.dispatch(Actions.setReviewerGroups(groups));
   return groups;
};

const postNotificationSettings = async (techSmithId: string, notificationSettings: NotificationSettingsModel): Promise<void> => {
   const oldNotificationSettings: NotificationSettingsModel[] = Object.assign([], [Store.getState().commentNotificationSettings, Store.getState().replyNotificationSettings]);
   const newNotificationSettings: NotificationSettingsModel[] = Object.assign([], oldNotificationSettings.map(setting => setting.eventType === notificationSettings.eventType ? notificationSettings : setting));

   try {
      await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithTscAuthToken(`${notificationsBaseUrl}/user/preferences/Feedback/${techSmithId}`, notificationSettings), 'PostNotificationSettings', Constants.statusCodes.accepted);
      reportNotificationChangeToGA(oldNotificationSettings, notificationSettings);
      Store.dispatch(Actions.setNotificationSettings(newNotificationSettings));
   } catch (e) {
      throw e;
   }
};

const getNotificationSettings = async (techSmithId: string): Promise<NotificationSettingsModel[]> => {
   try {
      const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithTscAuthToken(`${notificationsBaseUrl}/user/preferences/Feedback/${techSmithId}`), 'GetNotificationSettings', Constants.statusCodes.ok);
      const notificationSettings = response.data.userNotificationPreferences as NotificationSettingsModel[];
      Store.dispatch(Actions.setNotificationSettings(notificationSettings));
      return notificationSettings;
   } catch (e) {
      throw e;
   }
};

const setTheme = async (theme: string): Promise<void> => {
   if (Store.getState().themeName !== theme) {
      Store.dispatch(Actions.setInterfaceTheme(theme));
   }

   if (Config.user.isAuthenticated) {
      await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithMultipleAuths(`${baseUrl}/users/me`, {theme}), 'SetTheme', Constants.statusCodes.ok);
   } else {
      LocalStorageWrapper.setItem(Constants.localStorageKeys.themeName, theme);
   }
};

export default {
   initializeFeedbackUser,
   postAcceptTermsOfService,
   postNotificationSettings,
   setTheme,
   addReviewerGroup,
   deleteReviewerGroup,
   addReviewerToGroup,
   deleteReviewerFromGroup,
   getReviewersFromGroup,
   getReviewerGroups,
   updateReviewerGroupName,
   setSelfIdentifiedUser,
   forgetSelfIdentifiedUser,
   getNotificationSettings
};
